import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { csrfHeader } from '../csrf';
import { apiBaseUrl } from '../../lib/env';
import type { components } from '../schema';

export type Photo = components['schemas']['Photo'];
export type PhotoUploadResult = components['schemas']['PhotoUploadResult'];
export type ReactionSummary = components['schemas']['ReactionSummary'];

export function useUserPhotosCount(ownerId: string | undefined) {
  return useQuery({
    queryKey: ['photos', 'user', ownerId, 'count'],
    enabled: !!ownerId,
    queryFn: async () => {
      const { data, error } = await api.GET('/v5/users/{ownerId}/photos/count', {
        params: { path: { ownerId: ownerId! } },
      });
      if (error) throw error;
      return Number(data?.count ?? 0);
    },
  });
}

export function useUserPhotos(ownerId: string | undefined, take = 50, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['photos', 'user', ownerId, take],
    enabled: enabled && !!ownerId,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.GET('/v5/users/{ownerId}/photos', {
        params: { path: { ownerId: ownerId! }, query: { take, cursor: pageParam } },
      });
      if (error) throw error;
      return data!;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function usePhoto(photoId: string | undefined) {
  return useQuery({
    queryKey: ['photos', 'detail', photoId],
    enabled: !!photoId,
    queryFn: async () => {
      const { data, error } = await api.GET('/v5/photos/{photoId}', {
        params: { path: { photoId: photoId! } },
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; caption?: string; category?: number; type?: number }) => {
      // openapi-fetch supports multipart via FormData; we go raw fetch to keep
      // the cookie + X-Requested-With + base URL behaviour consistent.
      const fd = new FormData();
      fd.append('file', params.file);
      if (params.caption) fd.append('caption', params.caption);
      if (params.category !== undefined) fd.append('category', String(params.category));
      if (params.type !== undefined) fd.append('type', String(params.type));

      // Photo upload bypasses openapi-fetch (multipart body); we must
      // include the same CSRF header the api client's middleware adds.
      const headers: Record<string, string> = { 'X-Requested-With': 'haket-v5-ui', ...csrfHeader() };
      const res = await fetch(`${apiBaseUrl}/v5/photos`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      return (await res.json()) as PhotoUploadResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos'] });
      qc.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: string) => {
      const { error } = await api.DELETE('/v5/photos/{photoId}', {
        params: { path: { photoId } },
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['photos'] }),
  });
}

// Reaction summary for a photo — total, per-type counts, and the caller's
// own reaction (mine). Keyed by photo so the bar refreshes independently.
export function usePhotoReactions(photoId: string) {
  return useQuery({
    queryKey: ['photos', 'reactions', photoId],
    enabled: !!photoId,
    queryFn: async () => {
      const { data, error } = await api.GET('/v5/photos/{photoId}/reactions', {
        params: { path: { photoId } },
      });
      if (error) throw error;
      return (data ?? null) as ReactionSummary | null;
    },
  });
}

// Set/change the caller's reaction on a photo (PUT) or remove it (type null,
// DELETE). Raw fetch (mirroring comments) carries the CSRF header. Invalidates
// the photo's reaction summary so the bar's counts + mine refresh.
export function useReactToPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ photoId, type }: { photoId: string; type: number | null }) => {
      const headers = { ...csrfHeader(), 'Content-Type': 'application/json' };
      const url = `${apiBaseUrl}/v5/photos/${photoId}/reaction`;
      const res = type === null
        ? await fetch(url, { method: 'DELETE', credentials: 'include', headers })
        : await fetch(url, { method: 'PUT', credentials: 'include', headers, body: JSON.stringify({ type }) });
      if (!res.ok && res.status !== 204) throw new Error(`reaction failed: ${res.status}`);
    },
    onSuccess: (_data, { photoId }) => {
      qc.invalidateQueries({ queryKey: ['photos', 'reactions', photoId] });
    },
  });
}

export function photoUrl(ownerId: string, photoId: string) {
  // Lowercase the guids: files on disk are lowercase, but some sources (legacy
  // SQL uniqueidentifier strings) hand us UPPERCASE — an uppercase path 404s.
  return `${apiBaseUrl}/users/${ownerId.toLowerCase()}/album/${photoId.toLowerCase()}.jpg`;
}

export function photoThumbUrl(ownerId: string, photoId: string) {
  return `${apiBaseUrl}/users/${ownerId.toLowerCase()}/album/tmb/${photoId.toLowerCase()}.jpg`;
}

// Legacy profile pictures live at /legacy-photos/<lowercased-first-two-chars-
// of-username>/<original-cased-username>.<jpg|JPG>. Extension casing varies
// per file (~50/50 in the imported data), so consumers should set
// onError={(e) => e.currentTarget.src = profilePictureUrl(username, true)}
// to fall back to .JPG before showing a placeholder.
export function profilePictureUrl(username: string, uppercaseExt = false) {
  const prefix = username.slice(0, 2).toLowerCase();
  const ext = uppercaseExt ? 'JPG' : 'jpg';
  return `${apiBaseUrl}/legacy-photos/${prefix}/${username}.${ext}`;
}
